import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { teamApi } from '@/services/teamApi'
import { Button } from '@/components/ui/Button'

export default function TeamInviteAccept() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'idle' | 'accepting' | 'done' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const acceptedToken = useRef<string | null>(null)

  useEffect(() => {
    if (isLoading) return
    if (!token) {
      setStatus('error')
      setMessage('Missing invitation token.')
      return
    }
    if (!isAuthenticated) {
      const returnTo = encodeURIComponent(`/teams/invite/accept?token=${token}`)
      navigate(`/login?redirect=${returnTo}`, { replace: true })
      return
    }
    if (acceptedToken.current === token) return

    acceptedToken.current = token

    const accept = async () => {
      setStatus('accepting')
      try {
        await teamApi.acceptInvitation(token)
        setStatus('done')
      } catch (err: any) {
        setStatus('error')
        setMessage(
          err.response?.data?.errors?.token?.[0] ||
            err.response?.data?.errors?.email?.[0] ||
            err.response?.data?.message ||
            'Could not accept invitation.'
        )
      }
    }
    accept()
  }, [isAuthenticated, isLoading, token, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-md w-full rounded-lg border border-border p-6 space-y-4 text-center">
        <h1 className="text-xl font-semibold">Team invitation</h1>
        {status === 'accepting' || status === 'idle' ? (
          <p className="text-muted-foreground">Accepting invitation…</p>
        ) : null}
        {status === 'done' ? (
          <>
            <p className="text-foreground">You have joined the team.</p>
            <Button asChild>
              <Link to="/dashboard/team">Go to team</Link>
            </Button>
          </>
        ) : null}
        {status === 'error' ? (
          <>
            <p className="text-destructive">{message}</p>
            <Button variant="outline" asChild>
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </>
        ) : null}
      </div>
    </div>
  )
}
